import { Check, ChevronsUpDown, Loader2, Upload, X } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useUploadPostImage } from '../api/upload';
import { AuthContext, AuthContextType } from '@/features/app-layout/context';
import { Button } from '@/shared/ui/button.tsx';
import { queryClient } from "@/lib/react-query"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form.tsx"
import { z } from 'zod';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover.tsx"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/shared/ui/command.tsx"
import { type Catalog } from "../api"
import { cn } from '@/lib/utils';
import { Textarea } from '@/shared/ui/textarea.tsx';

type Props = {
  catalogData: Catalog[]
  onSelect: (val: { image_url: string, image_id: number, type: string }) => void,
  setShowUpload: () => void,
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const formSchema = z.object({
  file: z
    .instanceof(File, { message: "An image file is required" })
    .refine((file) => file.size <= MAX_FILE_SIZE, {
      message: "File size must be less than 10MB",
    }),
  catalogItem: z.string({ required_error: "Please select a catalog item" }).min(1, "Please select a catalog item"),
  prompt: z.string().min(1, { message: "Image Description is required" })
})

const UploadImageInput = ({ catalogData, onSelect, setShowUpload }: Props) => {


  const { username } = useContext<AuthContextType>(AuthContext)

  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onBlur",
    resolver: zodResolver(formSchema),
    defaultValues: {
      catalogItem: "",
    },
  })

  const catalogItem = useWatch({
    control: form.control,
    name: "catalogItem"
  })

  const [open, setOpen] = useState(false)

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const { uploadImage, isSuccess, data, isPending } = useUploadPostImage()

  useEffect(() => {

    if (isSuccess && data) {
      // setImage(data.image_path)
      queryClient.invalidateQueries({
        queryKey: ["gallery"]
      })
      onSelect({ image_url: data.image_path, image_id: data.image_id, type: catalogItem })
      setShowUpload()
    }

  }, [isSuccess])

  const { watch, setValue, formState, resetField } = form;

  const watchedFile = watch("file")

  // Update preview when file changes
  useEffect(() => {
    if (watchedFile instanceof File) {
      const url = URL.createObjectURL(watchedFile)
      setPreviewUrl(url)

      // Cleanup function to revoke object URL
      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [watchedFile])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null
    if (selectedFile) {
      setValue("file", selectedFile, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }
  }

  const handleRemoveFile = () => {
    resetField("file")
    setPreviewUrl(null)
  }

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    uploadImage({ media_file: data.file, user: username!, prompt: data.prompt, catalogue: data.catalogItem })
  }

  return (
    <div className="w-full p-6 border rounded-xl h-full">
      <div className="flex flex-col md:flex-row gap-8 items-center h-full">
        {/* Left Section - Upload Controls */}
        <div className="flex-1 space-y-4">
          <h2 className="text-2xl font-semibold">Upload Image</h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* File Upload Field */}
              <FormField
                control={form.control}
                name="file"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <div
                          className={`relative flex flex-col items-center justify-center rounded-lg border ${formState.errors.file ? "border-red-500 bg-red-50" : "border-dashed border-gray-700 bg-gray-200"} p-6 text-center cursor-pointer hover:bg-gray-300 transition-colors h-40`}
                        >
                          <Upload
                            className={`h-10 w-10 mb-2 ${formState.errors.file ? "text-red-500" : "text-gray-800"}`}
                          />
                          <div className="text-lg font-medium">Upload an image</div>
                          <p className="text-xs text-gray-700 mt-1">PNG, JPG or WEBP up to 10MB</p>
                          <input
                            type="file"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={handleFileChange}
                          />
                        </div>
                        <FormMessage />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* File Details (when file is selected) */}
              {watchedFile && (
                <div className="flex items-center justify-between p-3 border rounded-md bg-gray-50">
                  <div>
                    <p className="text-sm font-medium">
                      {watchedFile.name.length > 20 ? `${watchedFile.name.slice(0, 20)}...` : watchedFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">{(watchedFile.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <Button variant="outline" type="button" size="sm" onClick={handleRemoveFile}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {/* Catalog Item Select */}
              <FormField
                control={form.control}
                name="catalogItem"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Popover open={open} onOpenChange={setOpen} modal>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={`w-full justify-between h-10 ${formState.errors.catalogItem ? "border-red-500 ring-red-500" : ""
                              }`}
                            aria-expanded={open}
                          >
                            {field.value
                              ? catalogData.find((item) => item.unique_identifier === field.value)?.name
                              : "Select Product..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 md:w-[500px] lg:w-[800px]">
                          <Command>
                            <CommandInput placeholder="Search Product..." className="h-9" />
                            <CommandList>
                              <CommandEmpty>No Item found.</CommandEmpty>
                              <CommandGroup>
                                {catalogData.map((item) => (
                                  <CommandItem
                                    key={item.unique_identifier}
                                    value={item.name}
                                    onSelect={() => {
                                      setValue("catalogItem", item.unique_identifier, {
                                        shouldValidate: true,
                                      })
                                      setOpen(false)
                                    }}
                                    className="flex items-center gap-2"
                                  >
                                    <img
                                      src={item.image_url || "/placeholder.svg"}
                                      alt={item.name}
                                      className="w-6 h-6 object-contain"
                                    />
                                    {item.name}
                                    <Check
                                      className={cn(
                                        "ml-auto h-4 w-4",
                                        field.value === item.unique_identifier ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                  </FormItem>
                )}
              />
              {/* Image description */}
              <FormField
                control={form.control}
                name="prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="describe the image..." {...field} className='resize-none' />
                    </FormControl>
                  </FormItem>
                )}
              />
              {/* Upload Button */}
              <Button
                type="submit"
                disabled={isPending || !form.formState.isValid}
                variant="outline"
                className="w-32 rounded-full border border-purple-500 font-poppins tracking-wide"
              >
                {isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Uploading
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </form>
          </Form>
        </div>

        {/* Vertical Divider */}
        <div className="hidden md:block w-px bg-gray-200 self-stretch"></div>

        {/* Right Section - Image Preview */}
        <div className="flex-1 flex items-center justify-center border rounded-lg p-4 min-h-[300px]">
          {previewUrl ? (
            <div className="relative w-full h-full min-h-[250px]">
              <img src={previewUrl || "/placeholder.svg"} alt="Preview" className="object-contain" />
            </div>
          ) : (
            <div className="text-gray-400 text-lg">Image Preview</div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UploadImageInput;
